import { useEffect, useState } from "react";
import { api } from "../lib/api";
import { Toast } from "../components/Toast";
import { BrandLoader } from "../components/BrandLoader";
import { useDelayedLoader } from "../hooks/useDelayedLoader";

type Prefs = {
  view_alerts: string;
  download_alerts: string;
  nda_signed: string;
  unauthorized_access: string;
  daily_digest: number;
  weekly_digest: number;
};

export function NotificationSettingsPage() {
  const [prefs, setPrefs] = useState<Prefs | null>(null);
  const [prefsError, setPrefsError] = useState<string | null>(null);
  const [alertEmail, setAlertEmail] = useState("cursor@sensey.ch");
  const [toast, setToast] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const showPrefsLoader = useDelayedLoader(!prefs && !prefsError);

  useEffect(() => {
    api<{ preferences: Prefs; alertRecipientEmail?: string }>("/api/v1/notifications/preferences")
      .then((r) => {
        setPrefs(r.preferences);
        if (r.alertRecipientEmail) setAlertEmail(r.alertRecipientEmail);
      })
      .catch((e) => setPrefsError(String((e as Error).message || e)));
  }, []);

  const save = async (patch: Record<string, unknown>) => {
    setBusy(true);
    try {
      const res = await api<{ preferences: Prefs; alertRecipientEmail?: string }>("/api/v1/notifications/preferences", {
        method: "PATCH",
        body: JSON.stringify(patch),
      });
      setPrefs(res.preferences);
      if (res.alertRecipientEmail) setAlertEmail(res.alertRecipientEmail);
      setToast("Preferences saved");
    } catch (e) {
      setToast(String((e as Error).message || e));
    } finally {
      setBusy(false);
    }
  };

  if (prefsError) {
    return (
      <div>
        <h1 className="page-title">Alerts</h1>
        <p className="muted">{prefsError}</p>
      </div>
    );
  }

  if (!prefs) {
    return (
      <div>
        <h1 className="page-title">Alerts</h1>
        {showPrefsLoader ? (
          <div className="brand-loader-inline">
            <BrandLoader label="Loading preferences…" />
          </div>
        ) : null}
      </div>
    );
  }

  return (
    <div>
      <h1 className="page-title">Alerts</h1>
      <p className="page-lead" style={{ marginBottom: 8 }}>Choose when we notify you about visitor activity.</p>
      <p className="muted" style={{ margin: "0 0 18px", fontSize: 12.5 }}>
        Alerts go to <strong>{alertEmail}</strong>. NDA signed emails also reach allowlisted admins with a PDF receipt.
      </p>

      <section className="panel stack" style={{ maxWidth: 480, gap: 14 }}>
        <label className="stack">
          Document views
          <select
            className="input"
            value={prefs.view_alerts === "realtime" ? "batched" : prefs.view_alerts}
            disabled={busy}
            onChange={(e) => save({ viewAlerts: e.target.value }).catch(console.error)}
          >
            <option value="batched">Every 2 hours</option>
            <option value="off">Off</option>
          </select>
        </label>
        <label className="stack">
          Downloads
          <select
            className="input"
            value={prefs.download_alerts === "realtime" ? "batched" : prefs.download_alerts}
            disabled={busy}
            onChange={(e) => save({ downloadAlerts: e.target.value }).catch(console.error)}
          >
            <option value="batched">Every 2 hours</option>
            <option value="off">Off</option>
          </select>
        </label>
        <label className="stack">
          NDA signed
          <select
            className="input"
            value={prefs.nda_signed}
            disabled={busy}
            onChange={(e) => save({ ndaSigned: e.target.value }).catch(console.error)}
          >
            <option value="realtime">Real-time email</option>
            <option value="off">Off</option>
          </select>
        </label>
        <p className="muted" style={{ margin: 0, fontSize: 12.5 }}>
          Views and downloads are grouped into one email every 2 hours. NDA signed alerts are sent immediately with a PDF
          receipt.
        </p>
        <label className="row" style={{ gap: 8 }}>
          <input
            type="checkbox"
            checked={Boolean(prefs.daily_digest)}
            disabled={busy}
            onChange={(e) => save({ dailyDigest: e.target.checked }).catch(console.error)}
          />
          Daily digest email (Spielmann only)
        </label>
        <label className="row" style={{ gap: 8 }}>
          <input
            type="checkbox"
            checked={Boolean(prefs.weekly_digest)}
            disabled={busy}
            onChange={(e) => save({ weeklyDigest: e.target.checked }).catch(console.error)}
          />
          Weekly digest email (Spielmann only)
        </label>
        <p className="muted" style={{ margin: 0, fontSize: 12 }}>
          Digest emails cover Spielmann Dataroom activity only. Black Coffee Group is opted out.
        </p>
      </section>

      {toast ? <Toast message={toast} onDone={() => setToast(null)} /> : null}
    </div>
  );
}
